// AddStepToVsmModal — adds a single flow statement as a step into an
// existing VSM process or a brand-new one. Reached from the stopwatch
// button on a Flow card. Hands the assembled vsm back to ArcPilotShell
// via onSave, which routes it through useSession's saveVsm.

import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import type { Vsm, VsmProcess } from '../utils/flowModel';
import '../styles/AddStepToVsmModal.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // The flow statement id being added. null when the modal is closed.
  statementId: number | null;
  // Display label for the statement (e.g. "SubmitMortgageApplication")
  // — shown in the modal header so the user knows what they're adding.
  statementLabel: string;
  vsm: Vsm;
  onSave: (nextVsm: Vsm) => Promise<{ ok: boolean; error?: string }>;
}

const NEW_PROCESS_VALUE = '__new__';

function slugify(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'process';
}

function makeUniqueId(base: string, existing: VsmProcess[]): string {
  const taken = new Set(existing.map((p) => p.id));
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}-${n}`)) n++;
  return `${base}-${n}`;
}

const AddStepToVsmModal: React.FC<Props> = ({ isOpen, onClose, statementId, statementLabel, vsm, onSave }) => {
  const processes = vsm.processes;
  const [target, setTarget] = useState<string>(processes[0]?.id ?? NEW_PROCESS_VALUE);
  const [newProcessLabel, setNewProcessLabel] = useState('');
  const [ct, setCt] = useState('');
  const [wt, setWt] = useState('0');
  const [ca, setCa] = useState('100');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form to a sensible default every time the modal opens. Pick
  // the first existing process by default; if there are none, jump to
  // the "new process" path with the statement's label pre-filled so the
  // user can save with one click.
  useEffect(() => {
    if (!isOpen) return;
    setTarget(processes[0]?.id ?? NEW_PROCESS_VALUE);
    setNewProcessLabel(statementLabel || '');
    setCt('');
    setWt('0');
    setCa('100');
    setError(null);
  }, [isOpen, processes, statementLabel]);

  const isNew = target === NEW_PROCESS_VALUE;
  const canSave = useMemo(() => {
    if (statementId == null) return false;
    if (!ct.trim()) return false;
    if (isNew && !newProcessLabel.trim()) return false;
    return true;
  }, [statementId, ct, isNew, newProcessLabel]);

  if (!isOpen || statementId == null) return null;

  const handleSave = async () => {
    if (!canSave) return;
    const caNum = Math.max(0, Math.min(100, Math.round(Number(ca) || 0)));
    const newStep = {
      statementId,
      ct: ct.trim() || '0',
      wt: wt.trim() || '0',
      ca: caNum,
    };
    let nextProcesses: VsmProcess[];
    if (isNew) {
      const label = newProcessLabel.trim();
      const id = makeUniqueId(slugify(label), processes);
      nextProcesses = [...processes, { id, label, steps: [newStep] }];
    } else {
      nextProcesses = processes.map((p) => (
        p.id === target ? { ...p, steps: [...p.steps, newStep] } : p
      ));
    }
    setSaving(true);
    setError(null);
    const res = await onSave({ processes: nextProcesses });
    setSaving(false);
    if (!res.ok) {
      setError(res.error || 'Save failed');
      return;
    }
    onClose();
  };

  return (
    <div className="ap-modal-backdrop" onClick={onClose}>
      <div className="ap-modal vsm-add-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vsm-add-modal-head">
          <h3>Add to a Value Stream Map</h3>
          <button type="button" className="vsm-add-modal-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <p className="vsm-add-modal-sub">
          Statement <code>[{statementId}]</code> — {statementLabel || '(unnamed)'}
        </p>

        <label className="vsm-add-modal-label" htmlFor="vsm-add-target">Process</label>
        <select
          id="vsm-add-target"
          className="vsm-add-modal-input"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
        >
          {processes.map((p) => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
          <option value={NEW_PROCESS_VALUE}>+ New process…</option>
        </select>

        {isNew && (
          <>
            <label className="vsm-add-modal-label" htmlFor="vsm-add-new-label">New process name</label>
            <input
              id="vsm-add-new-label"
              className="vsm-add-modal-input"
              value={newProcessLabel}
              onChange={(e) => setNewProcessLabel(e.target.value)}
              placeholder="e.g. Submit mortgage application"
              autoFocus
            />
          </>
        )}

        <div className="vsm-add-modal-row">
          <div className="vsm-add-modal-field">
            <label className="vsm-add-modal-label" htmlFor="vsm-add-ct">Cycle time (CT)</label>
            <input
              id="vsm-add-ct"
              className="vsm-add-modal-input"
              value={ct}
              onChange={(e) => setCt(e.target.value)}
              placeholder="e.g. 30s, 2m, 1h, 1d"
            />
          </div>
          <div className="vsm-add-modal-field">
            <label className="vsm-add-modal-label" htmlFor="vsm-add-wt">Wait time (WT)</label>
            <input
              id="vsm-add-wt"
              className="vsm-add-modal-input"
              value={wt}
              onChange={(e) => setWt(e.target.value)}
              placeholder="e.g. 0, 4h, 2d"
            />
          </div>
          <div className="vsm-add-modal-field vsm-add-modal-field--narrow">
            <label className="vsm-add-modal-label" htmlFor="vsm-add-ca">%C&amp;A</label>
            <input
              id="vsm-add-ca"
              className="vsm-add-modal-input"
              type="number"
              min={0}
              max={100}
              value={ca}
              onChange={(e) => setCa(e.target.value)}
            />
          </div>
        </div>

        {error && <div className="vsm-add-modal-error">{error}</div>}

        <div className="vsm-add-modal-actions">
          <button type="button" className="ap-modal-cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="ap-modal-confirm"
            onClick={handleSave}
            disabled={!canSave || saving}
          >
            {saving ? 'Saving…' : 'Add to VSM'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddStepToVsmModal;
